import { useMemo } from "react";

import { faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  FieldPath,
  useFieldArray,
  useFormContext,
} from "react-hook-form";

import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { Input } from "src/base-components/Input";
import { Switch } from "src/base-components/Switch";
import {
  SortableField,
  SortableFieldsContext,
} from "src/base-components/schemaKit/SortableFields";
import {
  FeatureResponse,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import {
  Attribute,
  EntityPropertyRefTypeEnum,
  FeatureRefTypeEnum,
  SortDirection,
} from "src/clients/jobs";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { IconButton } from "src/design-system/Icon";
import { EntitySchemaResource } from "src/entities/queries";
import {
  buildEntityFieldOptions,
  EntityFieldOption,
  EntityFieldsDropdown,
} from "src/jobs/common/entitySource/EntityFieldsDropdown";
import {
  FormData,
  LIMIT_DEFAULT,
  LIMIT_MIN,
  SORT_MAX_KEYS,
} from "src/jobs/common/entitySource/types";

const SORTABLE_FIELD_TYPES = new Set<string>([
  PropertyDefinitionOutputTypeEnum.STRING,
  PropertyDefinitionOutputTypeEnum.NUMBER,
  PropertyDefinitionOutputTypeEnum.INTEGER,
  PropertyDefinitionOutputTypeEnum.BOOLEAN,
  PropertyDefinitionOutputTypeEnum.DATE,
  PropertyDefinitionOutputTypeEnum.DATETIME,
  PropertyDefinitionOutputTypeEnum.ENUM,
]);

const SORT_DIRECTION_OPTIONS = [
  { key: SortDirection.ASC, value: "Ascending" },
  { key: SortDirection.DESC, value: "Descending" },
];

const buildAttribute = (option: EntityFieldOption): Attribute => {
  if (option.sectionKey === "features") {
    return {
      type: FeatureRefTypeEnum.FEATURE_REF,
      feature_key: option.key,
    } as Attribute;
  }
  return {
    type: EntityPropertyRefTypeEnum.ENTITY_PROPERTY_REF,
    property_name: option.key,
  } as Attribute;
};

const getAttributeKey = (attribute: Attribute): string => {
  const a = attribute as { feature_key?: string; property_name?: string };
  return a.feature_key ?? a.property_name ?? "";
};

const buildDefaultSortKey = (options: EntityFieldOption[]) => {
  const firstOption = options[0];
  if (!firstOption) {
    return null;
  }
  return {
    attribute: buildAttribute(firstOption),
    direction: SortDirection.ASC,
  };
};

type SectionCardProps = {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  toggleDataLoc: string;
  children?: React.ReactNode;
};

const SectionCard: React.FC<SectionCardProps> = ({
  title,
  description,
  enabled,
  onToggle,
  toggleDataLoc,
  children,
}) => (
  <div className="flex flex-col gap-y-3 rounded-lg bg-gray-50 p-4">
    <div className="flex items-start justify-between gap-x-4">
      <div className="flex flex-col gap-y-1">
        <h3 className="font-inter-medium-14px text-gray-800">{title}</h3>
        <p className="font-inter-normal-12px text-gray-500">{description}</p>
      </div>
      <Switch dataLoc={toggleDataLoc} enabled={enabled} onChange={onToggle} />
    </div>
    {enabled && children}
  </div>
);

type SortListProps = {
  options: EntityFieldOption[];
};

const SortList: React.FC<SortListProps> = ({ options }) => {
  const form = useFormContext<FormData>();
  const { fields, append, remove, move } = useFieldArray<FormData, "order_by">({
    control: form.control,
    name: "order_by",
  });
  const orderBy = form.watch("order_by") ?? [];
  const usedKeys = orderBy.map((item) => getAttributeKey(item.attribute));

  const handleAddSort = () => {
    const available = options.filter((o) => !usedKeys.includes(o.key));
    const defaultKey = buildDefaultSortKey(available);
    if (defaultKey) {
      append(defaultKey);
    }
  };

  const canAddSort =
    fields.length < SORT_MAX_KEYS &&
    options.some((o) => !usedKeys.includes(o.key));

  return (
    <div className="flex flex-col gap-y-2">
      <SortableFieldsContext
        items={fields.map((field) => field.id)}
        onChangeOrder={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
      >
        {fields.map((field, index) => {
          const availableOptions = options.filter(
            (o) => o.key === usedKeys[index] || !usedKeys.includes(o.key),
          );
          return (
            <SortableField
              key={field.id}
              disabled={fields.length <= 1}
              id={field.id}
            >
              <div className="flex items-center gap-x-2">
                <div className="flex-1">
                  <Controller
                    control={form.control}
                    name={`order_by.${index}.attribute` as FieldPath<FormData>}
                    render={({ field: { value, onChange } }) => (
                      <EntityFieldsDropdown
                        dataLoc={`sort-column-${index}`}
                        error={undefined}
                        options={availableOptions}
                        value={getAttributeKey(value as Attribute)}
                        onChange={(selectedKey) => {
                          const option = options.find(
                            (o) => o.key === selectedKey,
                          );
                          if (option) {
                            onChange(buildAttribute(option));
                          }
                        }}
                      />
                    )}
                  />
                </div>
                <div className="flex-1">
                  <Controller
                    control={form.control}
                    name={`order_by.${index}.direction` as FieldPath<FormData>}
                    render={({ field: { value, onChange } }) => (
                      <FixedSimpleDropdown
                        buttonClassName="w-full"
                        buttonDataLoc={`sort-direction-${index}-button`}
                        dataLoc={`sort-direction-${index}`}
                        elements={SORT_DIRECTION_OPTIONS}
                        placeholder="Ascending"
                        selectedKey={
                          (value as SortDirection) ?? SortDirection.ASC
                        }
                        onSelect={onChange}
                      />
                    )}
                  />
                </div>
                <IconButton
                  aria-label="Remove sort"
                  data-loc={`remove-sort-${index}`}
                  disabled={fields.length <= 1}
                  icon={faTrashCan}
                  onClick={() => remove(index)}
                />
              </div>
            </SortableField>
          );
        })}
      </SortableFieldsContext>
      {canAddSort && (
        <div>
          <Button
            dataLoc="add-sort"
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={handleAddSort}
          >
            Add sort
          </Button>
        </div>
      )}
    </div>
  );
};

type Props = {
  entitySchema: EntitySchemaResource;
  features: FeatureResponse[];
};

export const SortAndLimitAccordion: React.FC<Props> = ({
  entitySchema,
  features,
}) => {
  const form = useFormContext<FormData>();
  const order_by = form.watch("order_by") ?? [];
  const limit = form.watch("limit");
  const limitEnabled = limit !== null;

  const options = useMemo(
    () =>
      buildEntityFieldOptions(entitySchema, features).filter((option) =>
        SORTABLE_FIELD_TYPES.has(option.fieldType),
      ),
    [entitySchema, features],
  );

  const handleToggleSort = (enabled: boolean) => {
    if (!enabled) {
      form.setValue("order_by", []);
      return;
    }
    const defaultKey = buildDefaultSortKey(options);
    form.setValue("order_by", defaultKey ? [defaultKey] : []);
  };

  const handleToggleLimit = (enabled: boolean) => {
    form.setValue("limit", enabled ? LIMIT_DEFAULT : null, {
      shouldValidate: true,
    });
  };

  const sortEnabled = order_by.length > 0;

  return (
    <div className="flex flex-col gap-y-3">
      <SectionCard
        description="Sort the resulting list of Entities by up to three properties. Properties listed first take priority over the ones below."
        enabled={sortEnabled}
        title="Sort Entities"
        toggleDataLoc="sort-toggle"
        onToggle={handleToggleSort}
      >
        <SortList options={options} />
      </SectionCard>
      <SectionCard
        description="Limit the resulting list of Entities to a certain number."
        enabled={limitEnabled}
        title="Limit Results"
        toggleDataLoc="limit-toggle"
        onToggle={handleToggleLimit}
      >
        <div className="flex flex-col gap-y-1.5">
          <label
            className="font-inter-medium-12px text-gray-800"
            htmlFor="limit-input"
          >
            Maximum number of Entities <span className="text-red-500">*</span>
          </label>
          <Controller
            control={form.control}
            name="limit"
            render={({ field: { value, onChange }, fieldState }) => (
              <>
                <Input
                  data-loc="limit-input"
                  errored={!!fieldState.error}
                  fullWidth
                  id="limit-input"
                  min={LIMIT_MIN}
                  type="number"
                  value={Number.isNaN(value) ? "" : (value ?? "")}
                  onChange={(e) => onChange(e.target.valueAsNumber)}
                />
                {fieldState.error && (
                  <ErrorHint dataLoc="limit-input-error">
                    {fieldState.error.message}
                  </ErrorHint>
                )}
              </>
            )}
            rules={{
              validate: (value) => {
                if (value === null) {
                  return true;
                }
                if (Number.isNaN(value)) {
                  return "Maximum number of Entities is required";
                }
                if (value < LIMIT_MIN) {
                  return `Maximum number of Entities must be at least ${LIMIT_MIN}`;
                }
                return true;
              },
            }}
          />
        </div>
      </SectionCard>
    </div>
  );
};
